import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, TruckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const emptyTrackingId = () => '';

const emptyPackage = () => ({
  trackingIds: [emptyTrackingId()],
  boxId: '',
  boxNumber: '',
});

// Mirrors the backend state machine (`VALID_STATUS_TRANSITIONS` in
// douro-make-backend/src/constants.js): only READY and ON_HOLD are legal
// source states for a DELIVERING transition. Earlier versions of this set
// allowed ONGOING/INSPECTION — which let users tick quotes in the modal and
// then eat a generic 400 from the backend. The drop handler in SlicerQuotes.jsx
// enforces the same guard; this set is a belt-and-braces check at confirm time.
const SHIPPABLE_STATUSES = new Set(['READY', 'ON_HOLD']);

export default function ShipmentModal({
  quote,
  group,
  subsetQuotes,
  preselectedQuoteIds,
  onConfirm,
  onCancel,
  loading = false,
}) {
  const isGroup = Boolean(group);
  const [packages, setPackages] = useState([emptyPackage()]);
  const [internalNotes, setInternalNotes] = useState('');
  const [boxes, setBoxes] = useState([]);
  const [error, setError] = useState('');
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(
    () => new Set(preselectedQuoteIds || [])
  );

  const toggleQuoteSelection = (id) => {
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setError('');
  };

  useEffect(() => {
    const fetchBoxes = async () => {
      try {
        const response = await api.get('/auth/boxes');
        setBoxes(response.data.data || response.data || []);
      } catch (err) {
        console.error('Failed to fetch boxes:', err);
      }
    };
    fetchBoxes();
  }, []);

  // ─── Package handlers ───────────────────────────────────────────────────────

  const addPackage = () => {
    setPackages([...packages, emptyPackage()]);
  };

  const removePackage = (index) => {
    setPackages(packages.filter((_, i) => i !== index));
  };

  const addTrackingId = (pkgIndex) => {
    const updated = [...packages];
    updated[pkgIndex] = {
      ...updated[pkgIndex],
      trackingIds: [...updated[pkgIndex].trackingIds, emptyTrackingId()],
    };
    setPackages(updated);
  };

  const removeTrackingId = (pkgIndex, trackIndex) => {
    const updated = [...packages];
    updated[pkgIndex] = {
      ...updated[pkgIndex],
      trackingIds: updated[pkgIndex].trackingIds.filter((_, i) => i !== trackIndex),
    };
    setPackages(updated);
  };

  const updateTrackingId = (pkgIndex, trackIndex, value) => {
    const updated = [...packages];
    const trackingIds = [...updated[pkgIndex].trackingIds];
    trackingIds[trackIndex] = value;
    updated[pkgIndex] = { ...updated[pkgIndex], trackingIds };
    setPackages(updated);
    setError('');
  };

  const updatePackageField = (pkgIndex, field, value) => {
    const updated = [...packages];
    updated[pkgIndex] = { ...updated[pkgIndex], [field]: value };
    setPackages(updated);
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate: at least 1 package with at least 1 non-empty tracking ID
    const shipments = packages.map((pkg) => {
      const trackingIds = pkg.trackingIds
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      const entry = { trackingIds };
      if (pkg.boxId) entry.boxId = pkg.boxId;
      if (pkg.boxNumber.trim()) entry.boxNumber = pkg.boxNumber.trim();
      return entry;
    });

    const validShipments = shipments.filter((s) => s.trackingIds.length > 0);

    if (validShipments.length === 0) {
      setError('At least one package with a tracking ID is required');
      return;
    }

    if (isGroup && selectedQuoteIds.size === 0) {
      setError('Select at least one quote to ship');
      return;
    }

    const trimmedNotes = internalNotes.trim();
    if (isGroup) {
      onConfirm({
        quoteIds: [...selectedQuoteIds],
        shipments: validShipments,
        ...(trimmedNotes ? { internalNotes: trimmedNotes } : {}),
      });
    } else {
      onConfirm({
        shipments: validShipments,
        ...(trimmedNotes ? { internalNotes: trimmedNotes } : {}),
      });
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  const inputClass =
    'w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="bg-cyan-50 px-6 py-4 border-b border-cyan-100">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TruckIcon className="h-6 w-6 text-cyan-600" />
                </div>
                <div className="ml-3">
                  {isGroup ? (
                    <>
                      <h3 className="text-lg font-semibold text-cyan-900">
                        Ship Group — {group.linkedOrderNumber || 'Standalone'}
                      </h3>
                      <p className="text-sm text-cyan-700">
                        {(subsetQuotes?.[0]?.contact?.name || group.contact?.name || '')}{' · '}
                        {selectedQuoteIds.size}/{(subsetQuotes || []).length} selected
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-cyan-900">
                        Ship Quote
                      </h3>
                      <p className="text-sm text-cyan-700">
                        Mark this quote as delivering
                      </p>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={onCancel}
                className="text-cyan-400 hover:text-cyan-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {!isGroup && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-900">
                    {quote.contact?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {quote._id}
                  </p>
                </div>
              )}

              {isGroup && (
                <div className="border border-slate-200 rounded-lg bg-white">
                  <div className="px-3 py-2 border-b border-slate-200 text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Quotes to ship
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(subsetQuotes || []).map((q) => {
                      const isShippable = SHIPPABLE_STATUSES.has(q.status);
                      const isChecked = selectedQuoteIds.has(q._id);
                      const total = q.priceSnapshot
                        ? `${(q.priceSnapshot.totalPriceInCents / 100).toFixed(2)} ${q.priceSnapshot.currency || 'EUR'}`
                        : null;
                      const readyBy = q.calculatedReadyBy
                        ? new Date(q.calculatedReadyBy).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })
                        : null;
                      return (
                        <label
                          key={q._id}
                          className={`flex items-start gap-2 px-3 py-2 ${isShippable ? 'cursor-pointer hover:bg-slate-50' : 'opacity-60 cursor-not-allowed'}`}
                        >
                          <input
                            type="checkbox"
                            disabled={!isShippable || loading}
                            checked={isChecked}
                            onChange={() => toggleQuoteSelection(q._id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-slate-900">×{q.quantity}</span>
                              {total && <span className="text-green-600 text-xs">{total}</span>}
                              {readyBy && <span className="text-xs text-slate-500">· ready {readyBy}</span>}
                            </div>
                            {q.internalNotes && (
                              <div className="text-xs text-yellow-700 line-clamp-1 mt-0.5" title={q.internalNotes}>
                                📝 {q.internalNotes}
                              </div>
                            )}
                            {!isShippable && (
                              <div className="text-xs text-red-600 mt-0.5">
                                Status {q.status} — cannot ship from this column
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Packages */}
              {packages.map((pkg, pkgIndex) => (
                <div
                  key={pkgIndex}
                  className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-700">
                      Package #{pkgIndex + 1}
                    </span>
                    {packages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePackage(pkgIndex)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Tracking IDs */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Tracking IDs <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {pkg.trackingIds.map((trackId, trackIndex) => (
                        <div key={trackIndex} className="flex gap-2">
                          <input
                            type="text"
                            value={trackId}
                            onChange={(e) =>
                              updateTrackingId(pkgIndex, trackIndex, e.target.value)
                            }
                            placeholder="e.g., RR123456789PT"
                            maxLength={100}
                            disabled={loading}
                            className={inputClass}
                          />
                          {pkg.trackingIds.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeTrackingId(pkgIndex, trackIndex)}
                              className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => addTrackingId(pkgIndex)}
                      disabled={loading}
                      className="mt-2 inline-flex items-center text-xs text-cyan-700 hover:text-cyan-900"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add Tracking ID
                    </button>
                  </div>

                  {/* Box Selection */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Box
                      </label>
                      <select
                        value={pkg.boxId}
                        onChange={(e) =>
                          updatePackageField(pkgIndex, 'boxId', e.target.value)
                        }
                        disabled={loading}
                        className={inputClass}
                      >
                        <option value="">None</option>
                        {boxes.map((box) => (
                          <option key={box._id} value={box._id}>
                            {box.name} ({box.innerDimensions?.length}x{box.innerDimensions?.width}x{box.innerDimensions?.height}mm)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Box Number
                      </label>
                      <input
                        type="text"
                        value={pkg.boxNumber}
                        onChange={(e) =>
                          updatePackageField(pkgIndex, 'boxNumber', e.target.value)
                        }
                        placeholder="e.g., BOX-1"
                        maxLength={100}
                        disabled={loading}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Package Button */}
              <button
                type="button"
                onClick={addPackage}
                disabled={loading}
                className="w-full py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-cyan-400 hover:text-cyan-700 transition-colors flex items-center justify-center gap-1"
              >
                <PlusIcon className="h-4 w-4" />
                Add Package
              </button>

              {/* Internal Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Internal Notes
                </label>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Optional notes..."
                  rows={2}
                  maxLength={1000}
                  disabled={loading}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 inline-flex items-center"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Shipping...
                  </>
                ) : (
                  <>
                    <TruckIcon className="h-4 w-4 mr-1.5" />
                    Ship Quote
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
